<template>
  <div class="admin-dashboard">
    <div class="page-header">
      <h1 class="page-title">대시보드</h1>
      <p class="page-subtitle">사이트 현황을 한눈에 확인하세요</p>
    </div>

    <!-- 통계 카드 -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon blue">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
          </svg>
        </div>
        <div class="stat-info">
          <p class="stat-label">전체 공고</p>
          <h3 class="stat-value">{{ stats.totalJobs }}</h3>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon yellow">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
        </div>
        <div class="stat-info">
          <p class="stat-label">프리미엄 공고</p>
          <h3 class="stat-value">{{ stats.premiumJobs }}</h3>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon green">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        </div>
        <div class="stat-info">
          <p class="stat-label">전체 회원</p>
          <h3 class="stat-value">{{ stats.totalUsers }}</h3>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon purple">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        </div>
        <div class="stat-info">
          <p class="stat-label">총 조회수</p>
          <h3 class="stat-value">{{ stats.totalViews.toLocaleString() }}</h3>
        </div>
      </div>
    </div>

    <!-- 최근 공고 & 최근 가입 -->
    <div class="dashboard-grid">
      <!-- 최근 공고 -->
      <div class="dashboard-card">
        <div class="card-header">
          <h2 class="card-title">최근 공고</h2>
          <router-link to="/admin/jobs" class="view-all">전체보기</router-link>
        </div>
        <div class="card-content">
          <div v-if="recentJobs.length === 0" class="empty">등록된 공고가 없습니다.</div>
          <div v-else class="list">
            <router-link 
              v-for="job in recentJobs" 
              :key="job.id" 
              :to="`/jobs/${job.id}`"
              class="list-item"
            >
              <div class="list-info">
                <h4>{{ job.title }}</h4>
                <p>{{ job.region }} · {{ formatDate(job.createdAt) }}</p>
              </div>
              <span v-if="job.isPremium" class="badge premium">P</span>
            </router-link>
          </div>
        </div>
      </div>

      <!-- 최근 가입 -->
      <div class="dashboard-card">
        <div class="card-header">
          <h2 class="card-title">최근 가입</h2>
          <router-link to="/admin/users" class="view-all">전체보기</router-link>
        </div>
        <div class="card-content">
          <div v-if="recentUsers.length === 0" class="empty">가입한 회원이 없습니다.</div>
          <div v-else class="list">
            <div v-for="user in recentUsers" :key="user.id" class="list-item">
              <div class="user-avatar">{{ user.email?.charAt(0).toUpperCase() }}</div>
              <div class="list-info">
                <h4>{{ user.email }}</h4>
                <p>{{ formatDate(user.createdAt) }}</p>
              </div>
              <span v-if="user.role === 'admin'" class="badge admin">관리자</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore'
import { db } from '@/services/firebase'

export default {
  name: 'AdminDashboardView',
  setup() {
    const stats = ref({
      totalJobs: 0,
      premiumJobs: 0,
      totalUsers: 0,
      totalViews: 0
    })
    const recentJobs = ref([])
    const recentUsers = ref([])

    const fetchStats = async () => {
      try {
        // 공고 통계
        const jobsSnapshot = await getDocs(collection(db, 'jobs'))
        stats.value.totalJobs = jobsSnapshot.size
        
        let premiumCount = 0
        let totalViews = 0
        jobsSnapshot.forEach(doc => {
          const data = doc.data()
          if (data.isPremium) premiumCount++
          totalViews += data.views || 0
        })
        stats.value.premiumJobs = premiumCount
        stats.value.totalViews = totalViews

        // 유저 통계
        const usersSnapshot = await getDocs(collection(db, 'users'))
        stats.value.totalUsers = usersSnapshot.size
      } catch (e) {
        console.error('통계 로드 실패:', e)
      }
    }

    const fetchRecentJobs = async () => {
      try {
        const q = query(
          collection(db, 'jobs'),
          orderBy('createdAt', 'desc'),
          limit(5)
        )
        const snapshot = await getDocs(q)
        recentJobs.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      } catch (e) {
        console.error('최근 공고 로드 실패:', e)
      }
    }

    const fetchRecentUsers = async () => {
      try {
        const q = query(
          collection(db, 'users'),
          orderBy('createdAt', 'desc'),
          limit(5)
        )
        const snapshot = await getDocs(q)
        recentUsers.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      } catch (e) {
        console.error('최근 유저 로드 실패:', e)
      }
    }

    const formatDate = (date) => {
      if (!date) return '-'
      if (date.toDate) date = date.toDate()
      return new Date(date).toLocaleDateString('ko-KR', {
        month: 'short',
        day: 'numeric'
      })
    }

    onMounted(() => {
      fetchStats()
      fetchRecentJobs()
      fetchRecentUsers()
    })

    return {
      stats,
      recentJobs,
      recentUsers,
      formatDate
    }
  }
}
</script>

<style scoped>
.admin-dashboard {
  padding: 40px;
}

.page-header {
  margin-bottom: 32px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 8px;
}

.page-subtitle {
  font-size: 15px;
  color: #6b7684;
}

/* 통계 카드 */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.stat-icon {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon.blue {
  background: #eff6ff;
  color: #3182f6;
}

.stat-icon.yellow {
  background: #fef9c3;
  color: #ca8a04;
}

.stat-icon.green {
  background: #dcfce7;
  color: #16a34a;
}

.stat-icon.purple {
  background: #f3e8ff;
  color: #9333ea;
}

.stat-label {
  font-size: 13px;
  color: #6b7684;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #191f28;
}

/* 대시보드 그리드 */
.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.dashboard-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f2f4f6;
}

.card-title {
  font-size: 16px;
  font-weight: 700;
  color: #191f28;
}

.view-all {
  font-size: 13px;
  color: #3182f6;
  font-weight: 500;
}

.card-content {
  padding: 8px;
}

.empty {
  padding: 40px;
  text-align: center;
  color: #8b95a1;
  font-size: 14px;
}

.list {
  display: flex;
  flex-direction: column;
}

.list-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  transition: background 0.15s;
}

.list-item:hover {
  background: #f9fafb;
}

.user-avatar {
  width: 36px;
  height: 36px;
  background: #3182f6;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
}

.list-info {
  flex: 1;
  min-width: 0;
}

.list-info h4 {
  font-size: 14px;
  font-weight: 500;
  color: #191f28;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.list-info p {
  font-size: 12px;
  color: #8b95a1;
}

.badge {
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
}

.badge.premium {
  background: #3182f6;
  color: #fff;
}

.badge.admin {
  background: #fef3c7;
  color: #b45309;
}

/* 반응형 */
@media (max-width: 1200px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .admin-dashboard {
    padding: 24px 16px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .dashboard-grid {
    grid-template-columns: 1fr;
  }
}
</style>